import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { RootState } from '../../store/store'
import LoadingSpinner from './LoadingSpinner'
import { evaluateRouteAccess } from '../../utils/accessFlow'

interface ProtectedRouteProps {
  children: React.ReactNode
  adminOnly?: boolean
  requirePlan?: boolean
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  adminOnly = false,
  requirePlan = true,
}) => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)

  const [checking, setChecking] = useState(!adminOnly && isAuthenticated)
  const [redirectTo, setRedirectTo] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    if (!isAuthenticated || adminOnly) {
      setChecking(false)
      return () => { mounted = false }
    }

    setChecking(true)
    setRedirectTo(null)
    evaluateRouteAccess(user, { requirePlan }).then(result => {
      if (!mounted) return
      setRedirectTo(result.allowed ? null : (result.redirectTo || '/login'))
      setChecking(false)
    })

    return () => { mounted = false }
  }, [adminOnly, isAuthenticated, requirePlan, user])

  if (!isAuthenticated) {
    return <Navigate to={adminOnly ? "/admin/login" : "/login"} replace />
  }

  if (adminOnly && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-950">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (redirectTo) return <Navigate to={redirectTo} replace />

  return <>{children}</>
}

export default ProtectedRoute
